import { 
  Layout, Type, Image, Video, Trophy, Calendar, 
  Users, Star, MessageSquare, Megaphone, BarChart3,
  Share2, Newspaper, ImageIcon, Radio
} from "lucide-react";

/**
 * Homepage section configuration constants
 * Defines built-in sections and custom section templates
 */

// Built-in sections that come pre-configured
export const BUILTIN_SECTIONS = [
  { id: "hero", name: "Hero / Next Event", icon: Layout, builtin: true, deletable: true, editable: true },
  { id: "top_stories", name: "Top Stories / News", icon: Newspaper, builtin: true, deletable: true },
  { id: "schedule", name: "Upcoming Schedule", icon: Calendar, builtin: true, deletable: true },
  { id: "poll", name: "Fan Poll", icon: BarChart3, builtin: true, deletable: true },
  { id: "athletes", name: "Athlete of the Week", icon: Trophy, builtin: true, deletable: true },
  { id: "gallery", name: "Photo Gallery", icon: ImageIcon, builtin: true, deletable: true },
  { id: "videos", name: "Recent Videos", icon: Video, builtin: true, deletable: true, editable: true },
  { id: "social", name: "Social Media", icon: Share2, builtin: true, deletable: true },
  { id: "scorebird_gamecast", name: "ScoreBird GameCast", icon: Radio, builtin: true, deletable: true },
  { id: "sponsors", name: "Sponsors", icon: Users, builtin: true, deletable: true, editable: true },
];

// Built-in section configurations (for sections that need custom data)
export const BUILTIN_SECTION_CONFIG = {
  hero: {
    defaultData: { 
      layout: "standard", // standard, compact
      background_image: "",
      image_position_x: 50,
      image_position_y: 50,
      image_scale: 100,
      image_blur: 0,
      overlay_opacity: 70
    },
    fields: [
      { key: "layout", label: "Layout Style", type: "select", options: ["standard", "compact"] },
      { key: "background_image", label: "Background Image", type: "hero_image_editor" }
    ]
  },
  sponsors: {
    defaultData: { title: "Our Sponsors", sponsors: [] },
    fields: [
      { key: "title", label: "Section Title", type: "text", placeholder: "e.g., Our Sponsors, Partners, Supported By" },
      { key: "sponsors", label: "Sponsors", type: "sponsor_list" }
    ]
  }
};

// Custom section templates that users can add
export const CUSTOM_TEMPLATES = [
  {
    id: "hero_banner",
    name: "Hero Banner",
    description: "Full-width hero with background image and CTA",
    icon: Layout,
    defaultData: { 
      title: "", 
      subtitle: "", 
      background_image: "", 
      image_position_x: 50, 
      image_position_y: 50, 
      image_scale: 100,
      image_blur: 0,
      cta_text: "", 
      cta_link: "" 
    },
    fields: [
      { key: "title", label: "Title", type: "text" },
      { key: "subtitle", label: "Subtitle", type: "textarea" },
      { key: "background_image", label: "Background Image", type: "hero_image_editor" },
      { key: "cta_text", label: "Button Text", type: "text" },
      { key: "cta_link", label: "Button Link", type: "text" }
    ]
  },
  {
    id: "countdown_banner",
    name: "Countdown Banner",
    description: "Event countdown timer with customizable design",
    icon: Calendar,
    defaultData: { title: "", event_name: "", event_date: "", event_time: "", background_image: "", cta_text: "", cta_link: "" },
    fields: [
      { key: "title", label: "Banner Title (e.g., 'Coming Soon')", type: "text" },
      { key: "event_name", label: "Event Name", type: "text" },
      { key: "event_date", label: "Event Date", type: "date" },
      { key: "event_time", label: "Event Time", type: "time" },
      { key: "background_image", label: "Background Image URL", type: "text" },
      { key: "cta_text", label: "Button Text", type: "text" },
      { key: "cta_link", label: "Button Link", type: "text" }
    ]
  },
  {
    id: "social_feed",
    name: "Social Feed Embed",
    description: "Embed Twitter/X, Facebook, or Instagram feeds",
    icon: Share2,
    defaultData: { title: "", platform: "twitter", embed_code: "", handle: "" },
    fields: [
      { key: "title", label: "Section Title", type: "text" },
      { key: "platform", label: "Platform", type: "select", options: ["twitter", "facebook", "instagram", "tiktok"] },
      { key: "handle", label: "Handle/Username", type: "text" },
      { key: "embed_code", label: "Embed Code (optional)", type: "textarea" }
    ]
  },
  {
    id: "text_block",
    name: "Text Block",
    description: "Rich text content section",
    icon: Type,
    defaultData: { title: "", content: "", alignment: "left" },
    fields: [
      { key: "title", label: "Title", type: "text" },
      { key: "content", label: "Content", type: "textarea" },
      { key: "alignment", label: "Alignment", type: "select", options: ["left", "center", "right"] }
    ]
  },
  {
    id: "image_gallery",
    name: "Image Gallery",
    description: "Grid of images",
    icon: Image,
    defaultData: { title: "", images: [] },
    fields: [
      { key: "title", label: "Section Title", type: "text" },
      { key: "images", label: "Images", type: "image_list" }
    ]
  },
  {
    id: "video_embed",
    name: "Video Embed",
    description: "Embedded YouTube video",
    icon: Video,
    defaultData: { title: "", video_url: "", description: "" },
    fields: [
      { key: "title", label: "Title", type: "text" },
      { key: "video_url", label: "YouTube URL", type: "text" },
      { key: "description", label: "Description", type: "textarea" }
    ]
  },
  {
    id: "featured_video",
    name: "Featured Video Hero",
    description: "Large featured video with gradient overlay, title, description and CTA",
    icon: Video,
    defaultData: { title: "", description: "", video_url: "", button_text: "Watch Now", button_link: "" },
    fields: [
      { key: "title", label: "Title", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "video_url", label: "YouTube URL", type: "text" },
      { key: "button_text", label: "Button Text", type: "text" },
      { key: "button_link", label: "Button Link", type: "text" }
    ]
  },
  {
    id: "recent_videos",
    name: "Recent Videos",
    description: "Display multiple videos with titles and dates",
    icon: Video,
    defaultData: { title: "Recent Videos", videos: [] },
    fields: [
      { key: "title", label: "Section Title", type: "text" },
      { key: "videos", label: "Videos", type: "recent_video_list" }
    ]
  },
  {
    id: "video_playlist",
    name: "Video Playlist",
    description: "Multiple videos in a carousel or grid",
    icon: Video,
    defaultData: { title: "", videos: [], layout: "grid" },
    fields: [
      { key: "title", label: "Section Title", type: "text" },
      { key: "layout", label: "Layout", type: "select", options: ["grid", "carousel"] },
      { key: "videos", label: "Videos", type: "video_list" }
    ]
  },
  {
    id: "stats_counter",
    name: "Stats Counter",
    description: "Animated statistics display",
    icon: Trophy,
    defaultData: { stats: [] },
    fields: [
      { key: "stats", label: "Statistics", type: "stats_list" }
    ]
  },
  {
    id: "featured_content",
    name: "Featured Content Cards",
    description: "Highlighted content in card format",
    icon: Star,
    defaultData: { title: "", cards: [] },
    fields: [
      { key: "title", label: "Section Title", type: "text" },
      { key: "cards", label: "Content Cards", type: "card_list" }
    ]
  },
  {
    id: "cta_banner",
    name: "Call-to-Action Banner",
    description: "Promotional banner with button",
    icon: Megaphone,
    defaultData: { title: "", description: "", button_text: "", button_link: "", background_color: "#f97316" },
    fields: [
      { key: "title", label: "Title", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "button_text", label: "Button Text", type: "text" },
      { key: "button_link", label: "Button Link", type: "text" },
      { key: "background_color", label: "Background Color", type: "color" }
    ]
  },
  {
    id: "newsletter_signup",
    name: "Newsletter Signup",
    description: "Email subscription form",
    icon: MessageSquare,
    defaultData: { title: "", description: "", button_text: "Subscribe", placeholder: "Enter your email" },
    fields: [
      { key: "title", label: "Title", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "button_text", label: "Button Text", type: "text" },
      { key: "placeholder", label: "Input Placeholder", type: "text" }
    ]
  },
  {
    id: "testimonials",
    name: "Testimonials",
    description: "Fan or partner quotes",
    icon: MessageSquare,
    defaultData: { title: "", testimonials: [] },
    fields: [
      { key: "title", label: "Section Title", type: "text" },
      { key: "testimonials", label: "Testimonials", type: "testimonial_list" }
    ]
  },
  {
    id: "sponsors_grid",
    name: "Sponsors Grid",
    description: "Grid of sponsor logos",
    icon: Star,
    defaultData: { title: "", sponsors: [] },
    fields: [
      { key: "title", label: "Section Title", type: "text" },
      { key: "sponsors", label: "Sponsors", type: "sponsor_list" }
    ]
  },
  {
    id: "team_roster",
    name: "Team Roster",
    description: "Display team members or staff",
    icon: Users,
    defaultData: { title: "", members: [] },
    fields: [
      { key: "title", label: "Section Title", type: "text" },
      { key: "members", label: "Team Members", type: "member_list" }
    ]
  },
  {
    id: "quick_links",
    name: "Quick Links",
    description: "Grid of icon links",
    icon: Layout,
    defaultData: { title: "", links: [] },
    fields: [
      { key: "title", label: "Section Title", type: "text" },
      { key: "links", label: "Links", type: "link_list" }
    ]
  }
];
